import { Button, Stack, StackProps, useToast } from '@chakra-ui/react';
import { copyText } from '@pelicargo/ui';
import { formatAddress } from '@pelicargo/utils';
import { useFormContext } from 'react-hook-form';
import { HiOutlineClipboardCopy } from 'react-icons/hi';

import { trpc } from '../../config/trpc';
import { Quote } from '../../utils/types';
import { ContactInput } from '../Autocomplete/ContactInput';
import { LabeledText } from '../LabeledText';
import { SimpleLabeledPopoverAddressInput } from '../SimpleLabeledPopoverAddressInput';
import { SimpleLabeledPopoverDockHoursInput } from '../SimpleLabeledPopoverDockHoursInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { BookingSection } from './AfrBookingForm';

type Props = StackProps & {
  quote: Quote;
};

export const ExwBookingForm = ({ quote, ...rest }: Props) => {
  const formContext = useFormContext();
  const deliveryAddress = formContext.watch('delivery_address');
  const toast = useToast();

  const { data: inland_dropoff_contact } = trpc.getContact.useQuery({ id: quote?.inland_dropoff_contact_id });

  const handleClick = async () => {
    const addressString = formatAddress(deliveryAddress);

    try {
      if (!addressString) throw new Error('No address to copy');

      copyText(addressString);
      toast({ title: 'Address copied!', status: 'success' });
    } catch (error) {
      toast({ title: 'Copy Failed', description: error?.message, status: 'error' });
    }
  };

  return (
    <Stack {...rest} spacing={6}>
      <Stack direction="row" flex={1} spacing={4}>
        <Stack flex={1} direction="column" spacing={6}>
          <BookingSection title="Booking Request Details">
            <SimpleLabeledPopoverInput
              name="pickup_ready_at"
              label="Pickup Ready At"
              inputType="date-picker"
              initialTimeZone="Etc/UTC"
              showTime={false}
              isHorizontal
              showClearButton
            />
            <SimpleLabeledPopoverDockHoursInput />
            <SimpleLabeledPopoverAddressInput name="delivery_address" />
            <Button
              variant="ghost"
              size="xs"
              colorScheme="blue"
              leftIcon={<HiOutlineClipboardCopy />}
              onClick={handleClick}
              alignSelf="flex-start"
            >
              Copy Address
            </Button>
          </BookingSection>
          <BookingSection title="References">
            <SimpleLabeledPopoverInput isHorizontal name="pickup_ref" label="Pickup Ref #" />
            <SimpleLabeledPopoverInput isHorizontal name="trucking_ref" label="Trucking Ref #" />
            <SimpleLabeledPopoverInput isHorizontal name="customer_ref" label="Customer Ref #" />
          </BookingSection>
          <BookingSection title="Details">
            <SimpleLabeledPopoverInput
              isHorizontal
              name="booking_instructions"
              label="Booking details"
              inputType="textarea"
            />
            <SimpleLabeledPopoverInput
              isHorizontal
              name="awb_instructions"
              label="AWB Instructions"
              inputType="textarea"
            />
            <SimpleLabeledPopoverInput
              isHorizontal
              name="additional_information"
              label="Additional details"
              inputType="textarea"
            />
          </BookingSection>
        </Stack>
        <Stack flex={1} direction="column" spacing={6}>
          <BookingSection title="Pick Up">
            <SimpleLabeledPopoverInput
              name="estimated_pickup_at"
              label="Estimated Pickup"
              inputType="date-picker"
              initialTimeZone="Etc/UTC"
              showTime
              isHorizontal
              showClearButton
            />
            <SimpleLabeledPopoverInput
              inputType="textarea"
              name="dropoff_instructions"
              label="Pickup details"
              isHorizontal
            />
            <LabeledText
              label="Email"
              textProps={{ noOfLines: 1 }}
              value={inland_dropoff_contact?.email || '-'}
              isCopyable
              isHorizontal
              py={1}
              px={2}
            />
            <LabeledText
              label="Phone"
              textProps={{ noOfLines: 1 }}
              value={inland_dropoff_contact?.phone_number || '-'}
              isHorizontal
              py={1}
              px={2}
            />
            <Stack mt={2}>
              <ContactInput
                name="inland_dropoff_contact_id"
                label="Inland Pickup Contact"
                defaultAirlineIds={[quote?.provider_id]}
                defaultAirportIds={[quote?.origin_airport_id]}
                my={1}
                mx={2}
              />
            </Stack>
          </BookingSection>
        </Stack>
      </Stack>
    </Stack>
  );
};
